import { useState, useEffect, useCallback } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { useToast } from '@/hooks/use-toast';

interface CollectionModuleSetting {
  id: string;
  setting_key: string;
  setting_value: string;
  setting_label: string;
}

interface CollectionCategory {
  id: string;
  name: string;
  name_ar?: string | null;
  slug: string;
  description: string | null;
  description_ar?: string | null;
  image_url: string | null;
  display_order: number;
  is_active: boolean;
}

interface CollectionSubcategory {
  id: string;
  category_id: string;
  name: string;
  name_ar?: string | null;
  slug: string;
  description: string | null;
  description_ar?: string | null;
  image_url: string | null;
  display_order: number;
  is_active: boolean;
}

interface CollectionProduct {
  id: string;
  name: string;
  name_ar?: string | null;
  slug: string;
  sku: string | null;
  perfume_code?: string | null;
  serial_number?: string | null;
  description: string | null;
  description_ar?: string | null;
  short_description: string | null;
  short_description_ar?: string | null;
  price_aed: number;
  price_50ml?: number | null;
  price_100ml?: number | null;
  compare_at_price_aed: number | null;
  category_id: string | null;
  subcategory_id: string | null;
  gender: string | null;
  occasion: string[] | null;
  size_ml: number;
  key_notes: string[] | null;
  accord: string | null;
  fragrance_family: string | null;
  similar_accords: string | null;
  top_notes?: string[] | null;
  middle_notes?: string[] | null;
  base_notes?: string[] | null;
  occasion_to_wear?: string | null;
  original_brand_name?: string | null;
  original_perfume_name?: string | null;
  shipping_details: string | null;
  return_policy: string | null;
  is_new: boolean;
  is_bestseller: boolean;
  is_signature: boolean;
  is_limited: boolean;
  is_featured: boolean;
  has_sample: boolean;
  sample_price_aed: number | null;
  linked_perfume_id: string | null;
  meta_title: string | null;
  meta_description: string | null;
  is_active: boolean;
  stock_quantity: number;
  preview_cta_enabled: boolean | null;
  live_cta_enabled: boolean | null;
  is_price_active?: boolean;
  is_50ml_active?: boolean;
  is_100ml_active?: boolean;
  is_sample_active?: boolean;
  created_at: string;
  category?: CollectionCategory;
  subcategory?: CollectionSubcategory;
  images?: CollectionProductImage[];
}

interface CollectionProductImage {
  id: string;
  product_id: string;
  image_url: string;
  image_path: string | null;
  alt_text: string | null;
  is_primary: boolean;
  display_order: number;
}

interface CollectionBanner {
  id: string;
  title: string;
  title_ar?: string | null;
  subtitle: string | null;
  subtitle_ar?: string | null;
  image_url: string | null;
  video_url: string | null;
  cta_text: string | null;
  cta_text_ar?: string | null;
  cta_link: string | null;
  display_order: number;
  is_active: boolean;
}

interface CollectionFeaturedSection {
  id: string;
  section_key: string;
  section_title: string;
  section_title_ar?: string | null;
  subtitle: string | null;
  subtitle_ar?: string | null;
  badge_key: string | null;
  display_order: number;
  is_active: boolean;
}

interface CollectionSignatureBox {
  id: string;
  name: string;
  description: string | null;
  base_price_aed: number;
  image_url: string | null;
  max_bottles: number;
  includes_greeting_card: boolean;
  greeting_card_price_aed: number | null;
  allows_custom_image: boolean;
  custom_image_price_aed: number | null;
  is_active: boolean;
  display_order: number;
}

export const useCollectionModuleStatus = () => {
  const [isModuleActive, setIsModuleActive] = useState(false);
  const [isPreviewMode, setIsPreviewMode] = useState(false);
  const [loading, setLoading] = useState(true);
  const { toast } = useToast();

  const fetchModuleStatus = useCallback(async () => {
    try {
      const { data, error } = await supabase
        .from('collection_module_settings')
        .select('*')
        .in('setting_key', ['module_active', 'preview_mode']);

      if (error) throw error;
      
      const activeStatus = data?.find(s => s.setting_key === 'module_active');
      const previewStatus = data?.find(s => s.setting_key === 'preview_mode');
      
      setIsModuleActive(activeStatus?.setting_value === 'true');
      setIsPreviewMode(previewStatus?.setting_value === 'true');
    } catch (error) {
      console.error('Error fetching module status:', error);
      setIsModuleActive(false);
      setIsPreviewMode(false);
    } finally {
      setLoading(false);
    }
  }, []);

  const toggleModuleStatus = async (newStatus: boolean) => {
    try {
      const { error } = await supabase
        .from('collection_module_settings')
        .update({ setting_value: newStatus ? 'true' : 'false' })
        .eq('setting_key', 'module_active');

      if (error) throw error;

      setIsModuleActive(newStatus);
      toast({
        title: newStatus ? 'Module Activated' : 'Module Deactivated',
        description: newStatus 
          ? 'Anaya Collection is now live on the website.' 
          : 'Anaya Collection has been hidden from the website.',
      });
    } catch (error) {
      console.error('Error toggling module status:', error);
      toast({
        title: 'Error',
        description: 'Failed to update module status.',
        variant: 'destructive',
      });
    }
  };

  const togglePreviewMode = async (newStatus: boolean) => {
    try {
      const { error } = await supabase
        .from('collection_module_settings')
        .update({ setting_value: newStatus ? 'true' : 'false' })
        .eq('setting_key', 'preview_mode');

      if (error) throw error;

      setIsPreviewMode(newStatus);
      toast({
        title: newStatus ? 'Preview Mode Enabled' : 'Preview Mode Disabled',
        description: newStatus 
          ? 'Anaya Collection is now visible in Preview mode for testing.' 
          : 'Preview mode has been disabled.',
      });
    } catch (error) {
      console.error('Error toggling preview mode:', error);
      toast({
        title: 'Error',
        description: 'Failed to update preview mode.',
        variant: 'destructive',
      });
    }
  };

  useEffect(() => {
    fetchModuleStatus();
  }, [fetchModuleStatus]);

  return { 
    isModuleActive, 
    isPreviewMode, 
    loading, 
    toggleModuleStatus, 
    togglePreviewMode,
    refetch: fetchModuleStatus 
  };
};

export const useCollectionCategories = () => {
  const [categories, setCategories] = useState<CollectionCategory[]>([]);
  const [loading, setLoading] = useState(true);

  const fetchCategories = useCallback(async (includeInactive = false) => {
    try {
      let query = supabase
        .from('collection_categories')
        .select('*')
        .order('display_order', { ascending: true });

      if (!includeInactive) {
        query = query.eq('is_active', true);
      }

      const { data, error } = await query;
      if (error) throw error;
      setCategories(data || []);
    } catch (error) {
      console.error('Error fetching categories:', error);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchCategories();
  }, [fetchCategories]);

  return { categories, loading, refetch: fetchCategories };
};

export const useCollectionSubcategories = (categoryId?: string) => {
  const [subcategories, setSubcategories] = useState<CollectionSubcategory[]>([]);
  const [loading, setLoading] = useState(true);

  const fetchSubcategories = useCallback(async (includeInactive = false) => {
    try {
      let query = supabase
        .from('collection_subcategories')
        .select('*')
        .order('display_order', { ascending: true });

      if (categoryId) {
        query = query.eq('category_id', categoryId);
      }

      if (!includeInactive) {
        query = query.eq('is_active', true);
      }

      const { data, error } = await query;
      if (error) throw error;
      setSubcategories(data || []);
    } catch (error) {
      console.error('Error fetching subcategories:', error);
    } finally {
      setLoading(false);
    }
  }, [categoryId]);

  useEffect(() => {
    fetchSubcategories();
  }, [fetchSubcategories]);

  return { subcategories, loading, refetch: fetchSubcategories };
};

export const useCollectionProducts = (filters?: {
  categoryId?: string;
  subcategoryId?: string;
  gender?: string;
  isFeatured?: boolean;
  isNew?: boolean;
  isBestseller?: boolean;
  search?: string;
}) => {
  const [products, setProducts] = useState<CollectionProduct[]>([]);
  const [loading, setLoading] = useState(true);

  const fetchProducts = useCallback(async (includeInactive = false) => {
    try {
      let query = supabase
        .from('collection_products')
        .select(`
          *,
          category:collection_categories(*),
          subcategory:collection_subcategories(*)
        `)
        .order('created_at', { ascending: false });

      if (!includeInactive) {
        query = query.eq('is_active', true);
      }

      if (filters?.categoryId) {
        query = query.eq('category_id', filters.categoryId);
      }
      if (filters?.subcategoryId) {
        query = query.eq('subcategory_id', filters.subcategoryId);
      }
      if (filters?.gender) {
        query = query.eq('gender', filters.gender);
      }
      if (filters?.isFeatured) {
        query = query.eq('is_featured', true);
      }
      if (filters?.isNew) {
        query = query.eq('is_new', true);
      }
      if (filters?.isBestseller) {
        query = query.eq('is_bestseller', true);
      }
      if (filters?.search) {
        query = query.or(`name.ilike.%${filters.search}%,description.ilike.%${filters.search}%`);
      }

      const { data, error } = await query;
      if (error) throw error;

      // Fetch images for each product
      if (data && data.length > 0) {
        const productIds = data.map(p => p.id);
        const { data: images } = await supabase
          .from('collection_product_images')
          .select('*')
          .in('product_id', productIds)
          .order('display_order', { ascending: true });

        const productsWithImages = data.map(product => ({
          ...product,
          images: images?.filter(img => img.product_id === product.id) || []
        }));
        
        setProducts(productsWithImages as CollectionProduct[]);
      } else {
        setProducts([]);
      }
    } catch (error) {
      console.error('Error fetching products:', error);
    } finally {
      setLoading(false);
    }
  }, [filters?.categoryId, filters?.subcategoryId, filters?.gender, filters?.isFeatured, filters?.isNew, filters?.isBestseller, filters?.search]);

  useEffect(() => {
    fetchProducts();
  }, [fetchProducts]);

  return { products, loading, refetch: fetchProducts };
};

export const useCollectionBanners = () => {
  const [banners, setBanners] = useState<CollectionBanner[]>([]);
  const [loading, setLoading] = useState(true);

  const fetchBanners = useCallback(async (includeInactive = false) => {
    try {
      let query = supabase
        .from('collection_banners')
        .select('*')
        .order('display_order', { ascending: true });

      if (!includeInactive) {
        query = query.eq('is_active', true);
      }

      const { data, error } = await query;
      if (error) throw error;
      setBanners(data || []);
    } catch (error) {
      console.error('Error fetching banners:', error);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchBanners();
  }, [fetchBanners]);

  return { banners, loading, refetch: fetchBanners };
};

export const useCollectionFeaturedSections = () => {
  const [sections, setSections] = useState<CollectionFeaturedSection[]>([]);
  const [loading, setLoading] = useState(true);

  const fetchSections = useCallback(async (includeInactive = false) => {
    try {
      let query = supabase
        .from('collection_featured_sections')
        .select('*')
        .order('display_order', { ascending: true });

      if (!includeInactive) {
        query = query.eq('is_active', true);
      }

      const { data, error } = await query;
      if (error) throw error;
      setSections(data || []);
    } catch (error) {
      console.error('Error fetching sections:', error);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchSections();
  }, [fetchSections]);

  return { sections, loading, refetch: fetchSections };
};

export const useCollectionSignatureBoxes = () => {
  const [boxes, setBoxes] = useState<CollectionSignatureBox[]>([]);
  const [loading, setLoading] = useState(true);

  const fetchBoxes = useCallback(async (includeInactive = false) => {
    try {
      let query = supabase
        .from('collection_signature_boxes')
        .select('*')
        .order('display_order', { ascending: true });

      if (!includeInactive) {
        query = query.eq('is_active', true);
      }

      const { data, error } = await query;
      if (error) throw error;
      setBoxes(data || []);
    } catch (error) {
      console.error('Error fetching signature boxes:', error);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchBoxes();
  }, [fetchBoxes]);

  return { boxes, loading, refetch: fetchBoxes };
};

export type {
  CollectionModuleSetting,
  CollectionCategory,
  CollectionSubcategory,
  CollectionProduct,
  CollectionProductImage,
  CollectionBanner,
  CollectionFeaturedSection,
  CollectionSignatureBox,
};
